import React, { useEffect, useState } from 'react'
import { Route, useLocation, useHistory } from 'react-router-dom'
import TrucksIcon from 'transfix-icons/build/TrucksIcon'
import { Button } from 'transfix-ui/components/Button'
import { ToastState, Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { BooleanParam } from 'use-query-params'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useCarrierApi } from 'apps/fleet-planner/hooks/useCarrierApi'
import { useCarrierMembershipApi } from 'apps/fleet-planner/hooks/useCarrierMembershipApi'
import { IMotorCarrier } from 'apps/fleet-planner/types'
import { IMobileWebViewParams } from 'apps/fleet-planner/types/mobileApp'
import { AddCarrierModal } from './AddCarrierModal'
import { CarriersTable } from './CarriersTable'
import { RemoveCarrierModal } from './RemoveCarrierModal'

const CARRIERS_LOCATION = '/carriers'
const CARRIERS_ADD_LOCATION = '/carriers/add'

export const CarriersPage = () => {
  const { trackEvent } = useFMSAnalytics()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { getUserMotorCarriers, userMotorCarriers } = useCarrierMembershipApi()
  const { getCarriers } = useCarrierApi()

  const [{ mobileApp }] = useQueryParams<IMobileWebViewParams>({
    mobileApp: BooleanParam,
  })
  const history = useHistory()
  const location = useLocation()

  const isAddCarrierModalOpen = location.pathname === CARRIERS_ADD_LOCATION

  const openAddCarrierModalHandler = () => {
    history.replace(CARRIERS_ADD_LOCATION)
  }
  const closeAddCarrierModalHandler = () => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('ADD_CARRIERS_CLOSED')
    } else {
      history.replace(CARRIERS_LOCATION)
    }
  }
  const [removedMotorCarrier, setRemovedCarrier] = useState<IMotorCarrier | undefined>(undefined)

  const updateSuccessHandler = (toastState: Omit<ToastState, 'toastOpen'>) => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('CARRIERS_ADDED')
    } else {
      openToast(toastState)
      // Re-fetch and trigger a reload whenever carrier data is successfully updated
      getUserMotorCarriers()
      getCarriers()
      history.replace(CARRIERS_LOCATION)
    }
  }

  // Fetch motor carriers for the current user on page mount
  useEffect(() => {
    getUserMotorCarriers()
    trackEvent('Carrier Management', 'CARRIERS_PAGE_VIEWED')
  }, [])

  return (
    <Route exact path={[CARRIERS_LOCATION, CARRIERS_ADD_LOCATION]}>
      {/* Navbar */}
      <TopNav title='Carriers'>
        <Button startIcon={<TrucksIcon />} color='primary' onClick={openAddCarrierModalHandler}>
          Add Carriers
        </Button>
      </TopNav>
      {/* Carrier Table */}
      <CarriersTable userMotorCarriers={userMotorCarriers} onRemoveCarrier={setRemovedCarrier} />
      {/* Modals */}
      {isAddCarrierModalOpen && (
        <AddCarrierModal
          onSuccess={updateSuccessHandler}
          onClose={closeAddCarrierModalHandler}
          initialCarriersDotNumbers={userMotorCarriers.map(umc => umc.motorCarrier.dotNumber)}
        />
      )}
      <RemoveCarrierModal
        motorCarrier={removedMotorCarrier}
        onSuccess={updateSuccessHandler}
        onClose={() => {
          setRemovedCarrier(undefined)
        }}
      />
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </Route>
  )
}
